import { computed, onMounted, watch } from 'vue'
import { useProject } from '@/hooks/useProject'
import { useBillingStore } from '@/store/stores/billing'
import { ensureProjectBillingLoaded } from '@/helpers/channels/ensureProjectBillingLoaded'
import {
    getProjectBillingStatus,
    isBillingBlocked as checkBillingBlocked,
    isProjectContentAvailable as checkProjectContentAvailable,
    ProjectBillingStatus,
} from '@/helpers/projectBillingStatus'

export function useProjectBillingStatus() {
    const { project } = useProject()
    const billingStore = useBillingStore()

    const loadBillingIfNeeded = async () => {
        if (!project.value?.id || !project.value.billingEnabled) return
        await ensureProjectBillingLoaded(project.value.id, project.value)
    }

    onMounted(() => {
        loadBillingIfNeeded()
    })

    watch(
        () => [project.value?.id, project.value?.billingEnabled] as const,
        () => {
            loadBillingIfNeeded()
        }
    )

    const billingBlock = computed(() => Boolean(project.value?.billingBlock))
    const currentTariff = computed(() => billingStore.getCurrentTariff(project.value?.id))
    const previousTariff = computed(() => billingStore.getPreviousTariff(project.value?.id))
    const isTrialActivatedBefore = computed(() => billingStore.getIsTrialActivatedBefore())

    const projectStatus = computed((): ProjectBillingStatus =>
        getProjectBillingStatus({
            billingEnabled: Boolean(project.value?.billingEnabled),
            billingBlock: billingBlock.value,
            currentTariff: currentTariff.value,
            previousTariff: previousTariff.value,
            isTrialActivatedBefore: isTrialActivatedBefore.value,
        })
    )

    const isBillingBlocked = computed(() => checkBillingBlocked(projectStatus.value))
    const isProjectContentAvailable = computed(() => checkProjectContentAvailable(projectStatus.value))

    return {
        projectStatus,
        isBillingBlocked,
        isProjectContentAvailable,
        billingBlock,
        currentTariff,
        previousTariff,
        isTrialActivatedBefore,
    }
}
